import { cn } from '@modules/lib/lib'

import { ColorChip } from './ColorChip'
import { ColorFilterControls } from './ColorFilterControls'
import { type ColorOption } from './colorOptions'
import { useColorFilter } from './useColorFilter'

interface ColorPaletteProps {
  onColorSelect: (colorValue: string) => void
  selectedColor?: string
  /** API から取得した色一覧（必須）。色マスターは Go 側 SSOT */
  colors: ColorOption[]
}

/**
 * 色名（label）を表示してよいか判定する.
 *
 * `label` は proto の `name` 由来だが、name 未設定の色は色コード（`value`）に
 * フォールバックしているため label === value のレコードが多数ある。そのまま出すと
 * 上段の色コードと重複表示になるので、空文字の場合と併せて弾く.
 */
const canShowColorLabel = (opt: ColorOption): boolean => opt.label !== '' && opt.label !== opt.value

/**
 * カラーパレットコンポーネント
 * メーカー、カラートーン、検索によるフィルタリング機能付き
 * @returns カラーパレットコンポーネント
 */
export const ColorPalette = ({ onColorSelect, selectedColor, colors }: ColorPaletteProps) => {
  const filter = useColorFilter(colors)
  const { filteredColors, totalColorCount } = filter

  const handleColorClick = (colorValue: string) => {
    onColorSelect(colorValue)
  }

  const handleColorKeyDown = (e: React.KeyboardEvent, colorValue: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onColorSelect(colorValue)
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 overflow-y-auto pb-4">
      <ColorFilterControls filter={filter} />

      <section className="flex flex-col gap-4">
        {/* 検索結果カウント */}
        <p className="shrink-0 text-sm font-medium text-[#71717A]">
          {filteredColors.length}色が見つかりました（全{totalColorCount}色中）
        </p>

        {/* 色リスト */}
        <div className="pb-1">
          <div className="grid grid-cols-2 gap-2">
            {filteredColors.map(opt => (
              <button
                aria-pressed={selectedColor === opt.value}
                className={cn(
                  'flex w-full items-start gap-3 rounded-lg border px-3 py-3 text-left transition-colors',
                  selectedColor === opt.value
                    ? 'border-emerald-500 bg-[#F4F4F5]'
                    : 'border-[#E4E4E7] bg-white',
                  'hover:bg-[#F4F4F5]',
                )}
                key={opt.id.toString()}
                onClick={() => handleColorClick(opt.value)}
                onKeyDown={e => handleColorKeyDown(e, opt.value)}
                type="button"
              >
                <ColorChip color={opt.color} rounded size="md" />
                <span className="flex min-w-0 flex-col">
                  <span className="break-words text-sm font-medium text-[#09090B]">
                    {opt.value}
                  </span>
                  {canShowColorLabel(opt) && (
                    <span className="break-words text-xs text-[#71717A]">{opt.label}</span>
                  )}
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}
